import { useQuery } from "@apollo/client/react";
import { createContext, type ReactNode, use, useMemo } from "react";
import {
  GetMeasurementUnitsDocument,
  type GetMeasurementUnitsQuery,
  type IngredientFragment,
} from "../../gql/generated/graphqlSchema.tsx";

type MeasurementUnit = GetMeasurementUnitsQuery["measurementUnits"][number];

type IngredientContextValue = {
  readonly ingredient: IngredientFragment;
  readonly setIngredient: (ingredient: IngredientFragment | undefined | null) => void;
  readonly refetch: () => Promise<unknown>;
  readonly units: readonly MeasurementUnit[];
  readonly readOnly: boolean;
};

const IngredientContext = createContext<IngredientContextValue | null>(null);

type IngredientProviderProps = Omit<IngredientContextValue, "units" | "readOnly"> & {
  readonly readOnly?: boolean;
  readonly children: ReactNode;
};

export const IngredientProvider = ({
  ingredient,
  setIngredient,
  refetch,
  readOnly = false,
  children,
}: IngredientProviderProps) => {
  const { data: unitsData } = useQuery(GetMeasurementUnitsDocument);
  const effectiveReadOnly = readOnly || ingredient.deleted;
  const value = useMemo(
    () => ({
      ingredient,
      setIngredient,
      refetch,
      units: unitsData?.measurementUnits ?? [],
      readOnly: effectiveReadOnly,
    }),
    [ingredient, setIngredient, refetch, unitsData, effectiveReadOnly],
  );
  return <IngredientContext value={value}>{children}</IngredientContext>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useIngredient = () => {
  const context = use(IngredientContext);
  if (!context) throw new Error("useIngredient must be used within an IngredientProvider");
  return context;
};
